import { FC, useState } from "react";

import { Stack, useTheme } from "@mui/material";
import { FormType, hasPermissionOnUserManagement } from "@simplyk/common";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardButtonSkeleton } from "@/dashboard-design-system/DashboardButton";
import { DashboardChip, DashboardChipSkeleton } from "@/dashboard-design-system/DashboardChip";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import ManageCollaboratorsModal from "@/features/SettingsPage/sharedComponents/ManageCollaboratorsModal/ManageCollaboratorsModal";
import {
  getCollaboratorRows,
  hasOnlyOwnerCollaborator,
} from "@/features/SettingsPage/sharedComponents/ManageCollaboratorsModal/helpers/manageCollaboratorsHelpers";
import { AddUser, MultipleUsers, User } from "@/icons/outlined";

interface CollaboratorsBlockProps {
  formId: string;
  formType: FormType;
}

export const CollaboratorsBlock: FC<CollaboratorsBlockProps> = ({ formId, formType }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { currentUser } = useCurrentUserContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const canManageUsers = hasPermissionOnUserManagement(currentUser?.newPermissions);

  const { data: formCollaborators, isPending } = trpc.getOrganizationMembers.useQuery(
    { formId, formType },
    { enabled: canManageUsers, refetchOnWindowFocus: false }
  );
  const collaboratorRows = getCollaboratorRows(formCollaborators, formId);
  const isSoloOwner = !isPending && hasOnlyOwnerCollaborator(collaboratorRows);

  if (!canManageUsers) {
    return null;
  }

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        sx={{
          backgroundColor: isSoloOwner ? theme.palette.surface.brand.quiet : theme.palette.surface.neutral.supershy,
          borderRadius: 1,
          px: 3,
          py: 2,
        }}
        data-test="advanced-settings-collaborators-block"
      >
        <Stack direction="column" gap={0.5}>
          {isPending ? (
            <Stack direction="row" alignItems="center" gap={1} data-test="collaborators-title-skeleton">
              <DashboardChipSkeleton shape="rounded" width="42px" />
              <DashboardSkeleton variant="text" width="220px" />
            </Stack>
          ) : isSoloOwner ? (
            <DashboardTypography variant="subtitle1" data-test="collaborators-solo-owner-title">
              {t("dashboard", "step.advancedSettings.collaborators.soloOwnerTitle")}
            </DashboardTypography>
          ) : (
            <DashboardTypography variant="subtitle1" component="div">
              <DashboardChip
                label={
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <DashboardIcon size="extraSmall" vibe="inherit">
                      <User />
                    </DashboardIcon>
                    <span>{collaboratorRows.length}</span>
                  </Stack>
                }
                vibe="positive"
                variant="outlined"
                shape="rounded"
                sx={{ verticalAlign: "middle", mr: 1 }}
                data-test="collaborators-count-chip"
              />
              {t("dashboard", "step.advancedSettings.collaborators.count", { count: collaboratorRows.length })}
            </DashboardTypography>
          )}
          <DashboardTypography variant="body2" vibe="text-neutral-moderate">
            {t("dashboard", "step.advancedSettings.collaborators.description")}
          </DashboardTypography>
        </Stack>

        {isPending ? (
          <DashboardButtonSkeleton size="small" width="140px" />
        ) : (
          <DashboardButton
            variant="tonal"
            vibe={isSoloOwner ? "brand" : "neutral"}
            size="small"
            endIcon={isSoloOwner ? <AddUser /> : <MultipleUsers />}
            onClick={() => setIsModalOpen(true)}
            data-test="manage-collaborators-button"
          >
            {isSoloOwner
              ? t("dashboard", "step.advancedSettings.collaborators.invite")
              : t("dashboard", "step.advancedSettings.collaborators.manage")}
          </DashboardButton>
        )}
      </Stack>

      {isModalOpen && (
        <ManageCollaboratorsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          source="form-editor"
          amplitudeEntrypoint="Form Editor - Advanced Settings"
          formEditorConfig={{ formId, formType }}
        />
      )}
    </>
  );
};
